import React, { useState } from 'react'
import { VerificationRequest } from '../types'
import { useTranslation } from '@structura/i18n'

interface VerificationDialogProps {
  show: boolean
  reportId: string
  onClose: () => void
  onVerify: (reportId: string, request: VerificationRequest) => Promise<void>
  verifying: boolean
}

const VerificationDialog: React.FC<VerificationDialogProps> = ({ show, reportId, onClose, onVerify, verifying }) => {
  const { t } = useTranslation()
  const [status, setStatus] = useState<number>(1)
  const [companyName, setCompanyName] = useState<string>('')
  const [personName, setPersonName] = useState<string>('')
  const [comments, setComments] = useState<string>('')
  const [companyLogo, setCompanyLogo] = useState<string>('')
  const [signature, setSignature] = useState<string>('')

  const handleFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result as string
        const base64 = result.split(',')[1]
        resolve(base64)
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const base64 = await handleFileToBase64(file)
      setCompanyLogo(base64)
    }
  }

  const handleSignatureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const base64 = await handleFileToBase64(file)
      setSignature(base64)
    }
  }

  const handleSubmit = async () => {
    const request: VerificationRequest = {
      status,
      companyName,
      personName,
      comments,
      companyLogoBase64: companyLogo || undefined,
      signatureBase64: signature || undefined
    }

    await onVerify(reportId, request)
    
    setStatus(1)
    setCompanyName('')
    setPersonName('')
    setComments('')
    setCompanyLogo('')
    setSignature('')
  }

  if (!show) return null

  return (
    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{t('beam.report.verifyTitle')}</h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={verifying}></button>
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label className="form-label">{t('beam.report.verificationStatus')}</label>
              <select className="form-select" value={status} onChange={(e) => setStatus(Number(e.target.value))} disabled={verifying}>
                <option value={1}>{t('beam.report.approved')}</option>
                <option value={2}>{t('beam.report.notApproved')}</option>
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">{t('beam.report.companyNameRequired')}</label>
              <input
                type="text"
                className="form-control"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder={t('beam.report.companyPlaceholder')}
                disabled={verifying}
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label">{t('beam.report.companyLogo')}</label>
              <input
                type="file"
                className="form-control"
                accept="image/*"
                onChange={handleLogoChange}
                disabled={verifying}
              />
              {companyLogo && <small className="text-muted">{t('beam.report.logoUploaded')}</small>}
            </div>

            <div className="mb-3">
              <label className="form-label">{t('beam.report.verifierNameRequired')}</label>
              <input
                type="text"
                className="form-control"
                value={personName}
                onChange={(e) => setPersonName(e.target.value)}
                placeholder={t('beam.report.verifierPlaceholder')}
                disabled={verifying}
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label">{t('beam.report.signature')}</label>
              <input
                type="file"
                className="form-control"
                accept="image/*"
                onChange={handleSignatureChange}
                disabled={verifying}
              />
              {signature && <small className="text-muted">{t('beam.report.signatureUploaded')}</small>}
            </div>

            <div className="mb-3">
              <label className="form-label">{t('beam.report.commentsRequired')}</label>
              <textarea
                className="form-control"
                rows={4}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder={t('beam.report.commentsPlaceholder')}
                disabled={verifying}
                required
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={verifying}>
              {t('beam.loads.cancel')}
            </button>
            <button
              type="button"
              className="btn beam-btn-primary"
              onClick={handleSubmit}
              disabled={verifying || !companyName || !personName || !comments}
            >
              {verifying ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  {t('beam.report.verifying')}
                </>
              ) : (
                t('beam.report.submitVerification')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VerificationDialog

